import { SIMILAR_BREEDS_VARIANTS, SimilarBreedsVariant } from './similarBreeds';

// Zero-based result indices: each insert renders after the result at that
// index. CORE-5924's tiers are written in slot numbers, which count banners as
// slots, so they do not line up one-for-one with these.
export const CONTROL_SCREENING_POSITION = 3;

// Control keeps the anchor the feed had before the experiment; the treatment
// arms sit one result later so the module has room above it.
export const CONTROL_ALERTS_POSITION = 9;
export const TREATMENT_ALERTS_POSITION = 10;

const MINIMUM_RESULTS_FOR_SCREENING = 4;

// Slot numbers in the comments are CORE-5924's, for checking against the table.
const SIMILAR_BREEDS_TIERS = [
  // slots 4 / 7
  { maxNearbyBreeders: 2, module: 2, screening: 4 },
  // slots 6 / 9
  { maxNearbyBreeders: 5, module: 4, screening: 6 },
  // slots 10 / 5
  { maxNearbyBreeders: 10, module: 7, screening: CONTROL_SCREENING_POSITION },
  // slots 16 / 5
  {
    maxNearbyBreeders: Infinity,
    module: 12,
    screening: CONTROL_SCREENING_POSITION
  }
];

interface FeedPositions {
  modulePosition: number | null;
  screeningPosition: number | null;
  alertsPosition: number | null;
}

interface ResolveFeedPositionsArgs {
  nearbyBreederCount: number;
  totalItemCount: number;
  variant?: SimilarBreedsVariant;
}

// The last tier is unbounded, so `find` matches for any finite count.
function tierFor(nearbyBreederCount: number) {
  return SIMILAR_BREEDS_TIERS.find(
    tier => nearbyBreederCount <= tier.maxNearbyBreeders
  )!;
}

export default function resolveFeedPositions({
  nearbyBreederCount,
  totalItemCount,
  variant = SIMILAR_BREEDS_VARIANTS.CONTROL
}: ResolveFeedPositionsArgs): FeedPositions {
  const lastPosition = totalItemCount - 1;

  // Short pages already pull alerts up to the last result, so every anchor
  // does. This can stack banners; CORE-5922 item 1 is open on that.
  function anchorAt(position: number) {
    return totalItemCount > 0 ? Math.min(position, lastPosition) : null;
  }

  const screeningPosition =
    totalItemCount >= MINIMUM_RESULTS_FOR_SCREENING
      ? CONTROL_SCREENING_POSITION
      : null;

  if (variant === SIMILAR_BREEDS_VARIANTS.CONTROL) {
    return {
      modulePosition: null,
      screeningPosition,
      alertsPosition: anchorAt(CONTROL_ALERTS_POSITION)
    };
  }

  const tier = tierFor(nearbyBreederCount);

  return {
    modulePosition: anchorAt(tier.module),
    screeningPosition:
      screeningPosition === null ? null : anchorAt(tier.screening),
    alertsPosition: anchorAt(TREATMENT_ALERTS_POSITION)
  };
}
